/** TanStack Query hooks over the real API (no mock data in production paths). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client.js";
import type {
  AlertRow,
  AppRow,
  AuditRow,
  BackupRow,
  CapabilityRow,
  CommandRow,
  ConsentRow,
  DeviceDetail,
  HealthRow,
  Parent,
  PolicyPreview,
  PolicyRow,
  ReportDetail,
  ReportRow,
  RequestRow,
  UsageRow,
} from "./types.js";

const qk = {
  me: ["me"] as const,
  devices: ["devices"] as const,
  device: (id: string) => ["device", id] as const,
  capabilities: (id: string) => ["device", id, "capabilities"] as const,
  apps: (id: string) => ["device", id, "apps"] as const,
  policies: (id: string) => ["device", id, "policies"] as const,
  usage: (id: string) => ["device", id, "usage"] as const,
  requests: (id: string) => ["device", id, "requests"] as const,
  consents: (id: string) => ["device", id, "consents"] as const,
  audioSessions: (id: string) => ["device", id, "audio-sessions"] as const,
  reports: (id: string) => ["device", id, "reports"] as const,
  alerts: ["alerts"] as const,
  health: ["system", "health"] as const,
  audit: ["audit-log"] as const,
  backups: ["system", "backups"] as const,
};

export const useMe = () =>
  useQuery({
    queryKey: qk.me,
    queryFn: () => api.get<Parent>("/auth/me"),
    retry: false,
  });

export const useDevices = (enabled = true) =>
  useQuery({
    queryKey: qk.devices,
    queryFn: () => api.get<DeviceDetail[]>("/devices"),
    enabled,
    refetchInterval: 30_000,
  });

export const useDevice = (id: string) =>
  useQuery({
    queryKey: qk.device(id),
    queryFn: () => api.get<DeviceDetail>(`/devices/${id}`),
    enabled: !!id,
    refetchInterval: 15000,
  });

export const useCapabilities = (id: string) =>
  useQuery({
    queryKey: qk.capabilities(id),
    queryFn: () => api.get<CapabilityRow[]>(`/devices/${id}/capabilities`),
    enabled: !!id,
    refetchInterval: 5_000,
  });

export const useApps = (id: string) =>
  useQuery({
    queryKey: qk.apps(id),
    queryFn: () => api.get<AppRow[]>(`/devices/${id}/apps`),
  });

export const usePolicies = (id: string) =>
  useQuery({
    queryKey: qk.policies(id),
    queryFn: () => api.get<PolicyRow[]>(`/devices/${id}/policies`),
  });

export const useUsage = (id: string) =>
  useQuery({
    queryKey: qk.usage(id),
    queryFn: () => api.get<UsageRow>(`/devices/${id}/usage`),
    enabled: !!id,
    refetchInterval: 15000,
  });

export const useRequests = (id: string) =>
  useQuery({
    queryKey: qk.requests(id),
    queryFn: () => api.get<RequestRow[]>(`/devices/${id}/requests`),
  });

export const useConsents = (id: string) =>
  useQuery({
    queryKey: qk.consents(id),
    queryFn: () => api.get<ConsentRow[]>(`/devices/${id}/recording-consents`),
  });

export const useAudioSessions = (id: string) =>
  useQuery({
    queryKey: qk.audioSessions(id),
    queryFn: () =>
      api.get<
        {
          id: string;
          capture_mode: string;
          capture_state: string;
          started_at: string;
          ended_at: string | null;
          completeness: number | null;
          failure_reason: string | null;
          uploaded_bytes: number;
        }[]
      >(`/devices/${id}/audio-sessions`),
  });

export const useReports = (id: string) =>
  useQuery({
    queryKey: qk.reports(id),
    queryFn: () => api.get<ReportRow[]>(`/devices/${id}/reports`),
  });

export const useAlerts = () =>
  useQuery({
    queryKey: qk.alerts,
    queryFn: () => api.get<AlertRow[]>("/alerts"),
  });

export const useHealth = () =>
  useQuery({
    queryKey: qk.health,
    queryFn: () => api.get<HealthRow>("/system/health"),
    refetchInterval: 30_000,
  });

export const useAuditLog = () =>
  useQuery({
    queryKey: qk.audit,
    queryFn: () => api.get<AuditRow[]>("/audit-log"),
  });

export const useBackups = () =>
  useQuery({
    queryKey: qk.backups,
    queryFn: () => api.get<BackupRow[]>("/system/backups"),
  });

const invalidate = (
  qc: ReturnType<typeof useQueryClient>,
  deviceId?: string,
) => {
  void qc.invalidateQueries({ queryKey: qk.devices });
  if (deviceId) {
    for (const key of [
      qk.device(deviceId),
      qk.apps(deviceId),
      qk.policies(deviceId),
      qk.requests(deviceId),
      qk.consents(deviceId),
      qk.audioSessions(deviceId),
      qk.reports(deviceId),
    ]) {
      void qc.invalidateQueries({ queryKey: key });
    }
  }
};

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<{ ok: boolean; mfa_required: boolean }>("/auth/login", body),
    onSuccess: () => void qc.invalidateQueries(),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean }>("/auth/logout"),
    onSuccess: () => qc.clear(),
  });
}

export function useAppRule(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      packageId,
      allowState,
    }: {
      packageId: string;
      allowState: string;
    }) =>
      api.put<AppRow>(
        `/devices/${deviceId}/apps/${encodeURIComponent(packageId)}/rule`,
        { allow_state: allowState },
      ),
    onSuccess: () => invalidate(qc, deviceId),
  });
}

export function useRemovePolicy(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{removed: boolean; revision: number}>(`/devices/${deviceId}/policies/${id}`),
    onSuccess: () => invalidate(qc, deviceId),
  });
}

export function useCreatePolicy(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      type: string;
      parameters: Record<string, unknown>;
      enabled?: boolean;
    }) =>
      api.post<{ id: string; revision: number; priority: number }>(
        `/devices/${deviceId}/policies`,
        body,
      ),
    onSuccess: () => invalidate(qc, deviceId),
  });
}

export function usePolicyPreview(deviceId: string) {
  return useMutation({
    mutationFn: (body: {
      now: string;
      usage_today?: Record<string, number>;
      caps?: Record<string, string>;
    }) => api.post<PolicyPreview>(`/devices/${deviceId}/policy-preview`, body),
  });
}

export function useDeviceAction(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      action,
      body,
    }: {
      action: string;
      body: Record<string, unknown>;
    }) =>
      api.post<{ command_id: string; state: string }>(
        `/devices/${deviceId}/actions/${action}`,
        body,
      ),
    onSuccess: () => invalidate(qc, deviceId),
  });
}

export function useCommandStatus(deviceId: string, commandId: string | null) {
  return useQuery({
    queryKey: ["command", deviceId, commandId],
    queryFn: () =>
      api.get<CommandRow>(`/devices/${deviceId}/actions/${commandId}`),
    enabled: !!commandId,
    refetchInterval: 3_000,
  });
}

export function useRequestDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      approve,
    }: {
      requestId: string;
      approve: boolean;
    }) =>
      api.post<{ state: string }>(`/requests/${requestId}/decision`, {
        approve,
      }),
    onSuccess: () => {
      invalidate(qc);
      void qc.invalidateQueries({ queryKey: ["device"] });
    },
  });
}

export function useCreateConsent(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ consent_id: string }>(
        `/devices/${deviceId}/recording-consents`,
        body,
      ),
    onSuccess: () => invalidate(qc, deviceId),
  });
}

export function useRevokeConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consentId: string) =>
      api.post<{ revoked: boolean }>(`/recording-consents/${consentId}/revoke`),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteAudioSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.del<{ deleted: boolean }>(`/audio-sessions/${sessionId}`),
    onSuccess: () => invalidate(qc),
  });
}

export function useExportAudio() {
  return useMutation({
    mutationFn: ({
      sessionId,
      justification,
    }: {
      sessionId: string;
      justification: string;
    }) =>
      api.post<{
        session_id: string;
        capture_state: string;
        total_bytes: number | null;
      }>(`/audio-sessions/${sessionId}/export`, { justification }),
  });
}

export function useSignedPlayback() {
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.get<{
        session_id: string;
        bundle: Record<string, unknown>;
        expires_at: number;
      }>(`/audio-sessions/${sessionId}/signed-playback`),
  });
}

export function useReportDetail(reportId: string | null) {
  return useQuery({
    queryKey: ["report", reportId],
    queryFn: () => api.get<ReportDetail>(`/reports/${reportId}`),
    enabled: !!reportId,
  });
}

export function useReviewFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      findingId,
      reviewState,
      note,
    }: {
      findingId: string;
      reviewState: string;
      note?: string;
    }) =>
      api.post<{ finding_id: string; review_state: string }>(
        `/findings/${findingId}/review`,
        { review_state: reviewState, note: note ?? null },
      ),
    onSuccess: () => invalidate(qc),
  });
}

export function useAckAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) =>
      api.post<{ status: string }>(`/alerts/${alertId}/acknowledge`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.alerts }),
  });
}

export function useEnrollmentToken(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ pairing_code: string; expires_in_seconds: number }>(
        `/devices/${deviceId}/enrollment-token`,
      ),
    onSuccess: () => invalidate(qc, deviceId),
  });
}


export type UpcomingEvents = {
  current_status?: { label: string; confirmed: boolean; restricted_apps: number | null; checked_at: string | null; detail: string } | null;
  events: { label: string; at: string; kind: string; detail: string }[];
  timezone: string; generated_at: string; allowance_fresh: boolean; remaining_ms: number | null;
};
export const useUpcoming = (id: string) => useQuery({
  queryKey: ["device", id, "upcoming"],
  queryFn: () => api.get<UpcomingEvents>(`/devices/${id}/upcoming`),
  enabled: !!id, refetchInterval: 5000,
});
